import React from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { useInView } from 'react-intersection-observer';
import {
  ProductItemWrapper,
  ProductListItemDetail,
  ProductCard,
} from '@fuww/library/src/Products';
import ImageSlider from '@fuww/library/src/ImageSlider';
import {
  ColorName,
  checkboxColors,
} from '@fuww/library/src/Checkbox/ColorCheckbox';
import coalesceWithEmptyString
  from '@fuww/library/src/lib/coalesceWithEmptyString';
import getPriceRange from '../../lib/getPriceRange';
import LinkAndAnchor from '../LinkAndAnchor';
import ImageWithUrls from '../ImageWithUrls';
import DynamicPredictions from './DynamicPredictions';
import { useSiteContext } from '../SiteContext';
import { ProductsConnectionQuery } from '../../lib/graphql/api/graphql';

const rootMargin = '512px';

export const productsListItemPropertyTypes = {
  marketId: PropTypes.number,
  standId: PropTypes.number,
  favoriteIds: PropTypes.shape({
    has: PropTypes.func.isRequired,
  }).isRequired,
  updateFavorites: PropTypes.func.isRequired,
  route: PropTypes.string,
  includeProfile: PropTypes.bool,
};

type ProductListItemProperties = (
  InferProps<typeof productsListItemPropertyTypes> &
  ProductsConnectionQuery['productsConnection']['edges'][number]['node']
);

const possibleColors = Object.keys(checkboxColors);

const ProductListItem = ({
  id,
  name,
  images,
  ageGroups,
  sizes,
  season,
  colors,
  brand,
  pricesWithCurrencies,
  orderQuantities,
  deliveryDays,
  prints,
  materials,
  updateFavorites,
  favoriteIds,
  marketId,
  standId,
  includeProfile,
  route,
}: ProductListItemProperties) => {
  const { locale } = useSiteContext();
  const [reference, inView] = useInView({
    rootMargin,
    triggerOnce: true,
  });

  const {
    name: brandName,
    profiles,
  } = { ...brand };

  return (
    <ProductItemWrapper
      data-test-brand={brandName}
      data-test-age-groups={ageGroups}
      data-test-sizes={sizes}
      data-test-season={season}
      data-test-colors={colors.map((color) => color.name)}
      data-test-min-price={pricesWithCurrencies[0]?.range?.minimum}
      data-test-min-quantity={orderQuantities?.minimum}
      data-test-max-delivery-days={deliveryDays?.maximum}
      data-test-prints={prints.map((print) => print.name)}
      data-test-materials={materials.map((material) => material.name)}
      key={id}
      ref={reference}
    >
      <LinkAndAnchor
        route={route}
        params={{
          ...(standId ? { standId: standId.toString() } : {}),
          ...(marketId ? { marketId: marketId.toString() } : {}),
          ...(includeProfile && profiles?.[0]
            ? { slug: profiles[0].page.slug } : {}),
          id: `${id}`,
        }}
      >
        <ProductCard
          productImage={(
            <ImageSlider
              images={
                images.map((image) => ({
                  key: image.id,
                  backgroundImageUrl: inView ? image.urls[4] : null,
                  image: (
                    <>
                      <ImageWithUrls
                        imageUrls={image.urls}
                        alt={coalesceWithEmptyString(name)}
                        sizes={`
                              (min-width: 1235px) 24vw,
                              (min-width: 939px) 35vw,
                              (min-width: 842px) 62vw,
                              (min-width: 617px) 47vw,
                              94vw
                            `}
                        srcSet={`${image.urls[1]
                        } 312w, ${image.urls[2]
                        } 624w, ${image.urls[3]
                        } 1007w`}
                        placeholderLoadStrategy="instant"
                      />
                      <DynamicPredictions url={image.urls[1]} />
                    </>
                  ),
                }))
              }
            />
          )}
          onFavoriteClick={(event) => {
            event.stopPropagation();
            event.preventDefault();
            updateFavorites(id);
          }}
          favorited={favoriteIds.has(id)}
        >
          <ProductListItemDetail
            brandName={coalesceWithEmptyString(brandName)}
            productName={coalesceWithEmptyString(name)}
            price={getPriceRange(pricesWithCurrencies, locale)}
            season={season}
            colors={(
              colors
                .map((color) => color.name)
                .filter((colorName) => possibleColors.includes(colorName))
            ) as ColorName[]}
          />
        </ProductCard>
      </LinkAndAnchor>
    </ProductItemWrapper>
  );
};

ProductListItem.propTypes = productsListItemPropertyTypes;

export default ProductListItem;
